import { generalInvitationService } from '@/services/friends/generalInvitationService';
import { logInviteSafe } from '@/utils/safeInviteLogging';

const PENDING_INVITE_CONTEXT_KEY = 'pendingInviteContext';
const INVITE_CHOICE_KEY = 'inviteChoice';
const PENDING_INVITE_CODE_KEY = 'pendingInviteCode';
const REHYDRATE_IN_PROGRESS_KEY = 'pendingInviteContextRehydrateInFlight';

type EnhancedInviteContext = {
  inviteCode: string;
  inviterId: string | null;
  inviterName: string;
  inviterAvatar: string | null;
  inviteType: string;
  clickedAt: string;
  inviteMessage: string;
  inviterEmail: string | null;
};

const storeInviteContext = (context: EnhancedInviteContext) => {
  const serialized = JSON.stringify(context);
  localStorage.setItem(PENDING_INVITE_CONTEXT_KEY, serialized);
  sessionStorage.setItem(PENDING_INVITE_CONTEXT_KEY, serialized);
};

export const persistInviteCode = (inviteCode: string) => {
  localStorage.setItem(PENDING_INVITE_CODE_KEY, inviteCode);
  sessionStorage.setItem(PENDING_INVITE_CODE_KEY, inviteCode);
};

const ensureInviteChoice = () => {
  const hasChoice = localStorage.getItem(INVITE_CHOICE_KEY) || sessionStorage.getItem(INVITE_CHOICE_KEY);
  if (!hasChoice) {
    localStorage.setItem(INVITE_CHOICE_KEY, 'connect');
    sessionStorage.setItem(INVITE_CHOICE_KEY, 'connect');
  }
};

export const rehydratePendingInviteContext = async (
  _stageLabel: string,
  fallbackInviteCode?: string | null
) => {
  try {
    const existing = localStorage.getItem(PENDING_INVITE_CONTEXT_KEY) || sessionStorage.getItem(PENDING_INVITE_CONTEXT_KEY);
    if (existing) {
      logInviteSafe('INVITE_STORAGE', { action: 'rehydrate_skip_existing' });
      return false;
    }

    let inviteCode =
      localStorage.getItem(PENDING_INVITE_CODE_KEY) ||
      sessionStorage.getItem(PENDING_INVITE_CODE_KEY);

    if (!inviteCode && fallbackInviteCode) {
      inviteCode = fallbackInviteCode;
      persistInviteCode(inviteCode);
    }

    if (!inviteCode) {
      logInviteSafe('INVITE_STORAGE', { action: 'rehydrate_skip_no_code' });
      return false;
    }

    if (sessionStorage.getItem(REHYDRATE_IN_PROGRESS_KEY) === 'true') {
      logInviteSafe('INVITE_STORAGE', { action: 'rehydrate_skip_in_flight' });
      return false;
    }

    sessionStorage.setItem(REHYDRATE_IN_PROGRESS_KEY, 'true');
    logInviteSafe('INVITE_STORAGE', { action: 'rehydrate_start' });

    const invite = await generalInvitationService.getInviteByCode(inviteCode);
    if (!invite) {
      logInviteSafe('INVITE_STORAGE', { action: 'rehydrate_failed' });
      return false;
    }

    persistInviteCode(inviteCode);

    const enhancedContext: EnhancedInviteContext = {
      inviteCode,
      inviterId: invite.creator_id ?? invite.creator?.id ?? null,
      inviterName: invite.creator_name || invite.creator?.name || 'Someone',
      inviterAvatar:
        invite.creator_photo_urls?.[0] ||
        invite.creator?.photo_urls?.[0] ||
        null,
      inviteType: invite.isLegacy ? 'legacy-general' : 'general',
      clickedAt: new Date().toISOString(),
      inviteMessage: invite.message || '',
      inviterEmail: invite.creator_email || invite.creator?.email || null
    };

    storeInviteContext(enhancedContext);
    ensureInviteChoice();

    logInviteSafe('INVITE_STORAGE', { action: 'rehydrate_success' });

    return true;
  } catch {
    logInviteSafe('INVITE_STORAGE', { action: 'rehydrate_error' });
    return false;
  } finally {
    sessionStorage.removeItem(REHYDRATE_IN_PROGRESS_KEY);
  }
};

export const logInviteStorageState = (_stageLabel: string) => {
  const pendingLocal = localStorage.getItem(PENDING_INVITE_CONTEXT_KEY);
  const pendingSession = sessionStorage.getItem(PENDING_INVITE_CONTEXT_KEY);
  const choiceLocal = localStorage.getItem(INVITE_CHOICE_KEY);
  const choiceSession = sessionStorage.getItem(INVITE_CHOICE_KEY);
  const codeLocal = localStorage.getItem(PENDING_INVITE_CODE_KEY);
  const codeSession = sessionStorage.getItem(PENDING_INVITE_CODE_KEY);

  logInviteSafe('INVITE_STORAGE', {
    action: 'storage_snapshot',
    localHasContext: !!pendingLocal,
    sessionHasContext: !!pendingSession,
    hasInviteChoice: !!(choiceLocal || choiceSession),
    hasInviteCode: !!(codeLocal || codeSession),
  });
};
